/**
 * Role-Permission Matrix (frontend)
 * ─────────────────────────────────────────────────────────────────────────
 * Source-of-truth for what each fixed admin role can access. Keep in sync
 * with the backend role guards on each endpoint.
 *
 * Roles:
 *   • super_admin    — full access to everything
 *   • manager        — everything except User Management (super_admin only)
 *   • reception      — Operations, Check-ins, Points (Scan/Manual), Scan Pass,
 *                      Bookings, Customers
 *   • marketing      — Dashboard, Events, Marketing, WhatsApp Campaigns,
 *                      Instagram Import, Customers (for segmentation)
 *   • gate_scan      — ONLY the Scan Pass page (kiosk-style)
 *   • finance_viewer — Dashboard, Payments, Reports (read-only)
 *   • special_ops    — Special Events (all tabs), Marketing, WhatsApp Campaigns,
 *                      Customers (for segmentation), Dashboard
 *
 * Path matching is *prefix* based — granting `/admin/marketing` also grants
 * `/admin/marketing/templates/...`, etc.
 */

// Per-role list of path-prefixes the user is allowed to visit.
// `*` is a wildcard meaning "every /admin/* route".
const ROLE_PATH_PREFIXES = {
  super_admin: ['*'],
  manager: [
    '/admin/dashboard',
    '/admin/operations',
    '/admin/checkins',
    '/admin/points',
    '/admin/payments',
    '/admin/pricing-rules',
    '/admin/events',
    '/admin/scan-pass',
    '/admin/bookings',
    '/admin/customers',
    '/admin/marketing',
    '/admin/promoters',
    '/admin/reports',
    '/admin/analytics',
    '/admin/server',
  ],
  reception: [
    '/admin/dashboard',
    '/admin/operations',
    '/admin/checkins',
    '/admin/points/scan',
    '/admin/points/manual-redeem',
    '/admin/scan-pass',
    '/admin/bookings',
    '/admin/customers',
  ],
  marketing: [
    '/admin/dashboard',
    '/admin/events',
    '/admin/marketing',         // includes /marketing/*, /marketing/whatsapp/*
    '/admin/customers',
    '/admin/analytics',
  ],
  gate_scan: [
    '/admin/scan-pass',
  ],
  finance_viewer: [
    '/admin/dashboard',
    '/admin/payments',
    '/admin/reports',
  ],
  special_ops: [
    '/admin/dashboard',
    '/admin/special-events',    // includes /bookings and /scan children
    '/admin/marketing',         // includes /marketing/whatsapp/*
    '/admin/customers',         // needed for segmentation
  ],
};

// Where each role should land after logging in (or when hitting `/admin`).
const ROLE_LANDING_PATH = {
  super_admin:    '/admin/dashboard',
  manager:        '/admin/dashboard',
  reception:      '/admin/operations',
  marketing:      '/admin/marketing',
  gate_scan:      '/admin/scan-pass',
  finance_viewer: '/admin/dashboard',
  special_ops:    '/admin/special-events',
};

/**
 * Returns true iff the given user role can visit the given pathname.
 * Always allows `/admin/login` so that the login screen is reachable
 * regardless of role / loading state.
 */
export function canAccessPath(role, pathname) {
  if (!pathname) return false;
  if (pathname === '/admin/login') return true;
  const prefixes = ROLE_PATH_PREFIXES[role];
  if (!prefixes) return false;
  if (prefixes.includes('*')) return true;
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/**
 * Default landing page for a given role. Falls back to /admin/dashboard
 * for unknown roles (defensive — server should never issue one).
 */
export function landingPathForRole(role) {
  return ROLE_LANDING_PATH[role] || '/admin/dashboard';
}
